import React, { useContext, useState, type KeyboardEvent } from "react";

import { IntlContext } from "context";
import { getCategoryVisual } from "constants/categories";
import type { CatalogService } from "types/service";
import type { ServiceInterface } from "types/service";
import { Badge } from "components/ui/badge";
import { MutedInset } from "components/ui/muted-inset";
import { cn } from "lib/utils";
import { ProviderDetailCard } from "./ProviderDetailCard";
import { ProviderServiceBringNotes, hasProviderServiceBringNotes } from "./ProviderServiceBringNotes";
import { ProviderServiceDetailDialog } from "./ProviderServiceDetailDialog";
import { ProviderServiceOptions, useProviderServiceOptionGroups } from "./ProviderServiceOptions";

export const PROVIDER_SERVICES_SECTION_ID = "provider-services-section";

interface Props {
    catalogServices: CatalogService[];
    provider: ServiceInterface["provider"];
    providerId: string;
    canContact?: boolean;
}

function openOnKeyboard(event: KeyboardEvent, open: () => void) {
    if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open();
    }
}

const ServiceCardMeta: React.FC<{ serviceData?: CatalogService["data"] }> = ({ serviceData }) => {
    const intl = useContext(IntlContext);
    const optionGroups = useProviderServiceOptionGroups(serviceData);
    const hasBringNotes = hasProviderServiceBringNotes(serviceData);

    if (optionGroups.length === 0 && !hasBringNotes) {
        return null;
    }

    return (
        <div className="mt-2 space-y-2 border-t border-border pt-2">
            {optionGroups.length > 0 && <ProviderServiceOptions serviceData={serviceData} compact />}
            {hasBringNotes && (
                <ProviderServiceBringNotes
                    serviceData={serviceData}
                    formatMessage={(key) => String(intl.formatMessage(key))}
                    className="space-y-1.5"
                />
            )}
        </div>
    );
};

export const ProviderServicesListCard: React.FC<Props> = ({
    catalogServices,
    provider,
    providerId,
    canContact = true,
}) => {
    const intl = useContext(IntlContext);
    const [selectedService, setSelectedService] = useState<CatalogService | null>(null);

    return (
        <>
            <ProviderDetailCard
                id={PROVIDER_SERVICES_SECTION_ID}
                title={intl.formatMessage("provider.view.section_services")}
            >
                <div className="space-y-3">
                    {catalogServices.map((svc) => {
                        const svcCatKey = String(svc.category ?? "").toLowerCase();
                        const { palette, Icon } = getCategoryVisual(svcCatKey);
                        const svcLabel = svc.subcategory
                            ? intl.formatMessage(`our_services.label.${svc.subcategory}`)
                            : intl.formatMessage(`category.${svcCatKey}`);

                        return (
                            <MutedInset
                                key={svc.id}
                                role="button"
                                tabIndex={0}
                                className={cn(
                                    "cursor-pointer border border-transparent transition-colors",
                                    "hover:border-border/50 hover:bg-muted/60",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                )}
                                onClick={() => setSelectedService(svc)}
                                onKeyDown={(event) => openOnKeyboard(event, () => setSelectedService(svc))}
                            >
                                <div className="flex items-center gap-3">
                                    <Badge variant="iconTile" palette={palette} size="tileMd">
                                        <Icon className="h-4 w-4" />
                                    </Badge>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold leading-tight text-foreground">
                                            {svcLabel}
                                        </p>
                                        {svc.description && (
                                            <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                                                {svc.description}
                                            </p>
                                        )}
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <p className="font-extrabold leading-tight text-foreground">
                                            {Number(svc.price).toFixed(2)}€
                                            <span className="text-xs font-normal text-muted-foreground">
                                                {intl.formatMessage("provider.card.price_per_hour")}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <ServiceCardMeta serviceData={svc.data} />
                            </MutedInset>
                        );
                    })}
                </div>
            </ProviderDetailCard>

            <ProviderServiceDetailDialog
                service={selectedService}
                providerFirstName={provider.first_name}
                providerId={providerId}
                canContact={canContact}
                open={selectedService !== null}
                onOpenChange={(open) => {
                    if (!open) setSelectedService(null);
                }}
            />
        </>
    );
};
